import { Box, Grid, Paper, useMediaQuery } from "@mui/material";
import { Field, Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { Base } from "../../base";
import { Inputs } from "../../formInputs";
import { useDispatch, useSelector } from "../../../store";
import { defaultParams } from "../../../types/searchParams";
import { debounce } from "lodash";
import theme from "../../../theme";
import { searchJob } from "../../../slices/job";
import ColumnDef from "../../../types/columnDef";
import {
  getGridDateOperators,
  getGridNumericOperators,
  getGridSingleSelectOperators,
  GridColumnVisibilityModel,
  GridFilterItem,
  GridFilterModel,
  GridLogicOperator,
} from "@mui/x-data-grid-premium";
import moment from "moment";
import JobReport from "../../../types/jobReport";
import {
  deleteFilterSet,
  getFilterSet,
  getFilterSets,
  postFilterSet,
} from "../../../slices/filterSet";
import KeyValue from "../../../types/keyValue";
import FilterSet from "../../../types/filterSet";
import { setReportFilter } from "../../../slices/report";

const ReportJobTable: React.FC = () => {
  const dispatch = useDispatch();
  const { jobSearchResults, searchStatus } = useSelector((store) => store.job);
  const { jobTypes } = useSelector((store) => store.jobType);
  const { jobStatuses } = useSelector((store) => store.jobStatus);
  const { filterSets, status: filterSetStatus } = useSelector(
    (store) => store.filterSets,
  );
  const { loggedInUser } = useSelector((store) => store.authentication);
  const { reportFilter } = useSelector((store) => store.report);
  const { simpleUserList } = useSelector((store) => store.user);
  const [mobileRows, setMobileRows] = useState<any[]>([]);
  const [reportStart, setReportStart] = useState<Date | undefined>(undefined);
  const [reportEnd, setReportEnd] = useState<Date | undefined>(undefined);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
    logicOperator: GridLogicOperator.And,
  });
  const [visibility, setVisibility] = useState<GridColumnVisibilityModel>({
    title: true,
    // created: true,
    status: true,
    complete: true,
    jobType: true,
    hours: true,
    workedBy: true,
    actions: true,
  });
  const [filterSetModal, setFilterSetModal] = useState<{
    mode?: "load" | "save" | "delete";
  }>();
  var date = new Date();
  const phone = useMediaQuery(theme.breakpoints.down("sm"));
  const statusOptions = [
    ...jobStatuses
      .filter((s) =>
        jobTypes
          .filter(
            (j) =>
              j.logHours &&
              loggedInUser?.assignedJobTypes?.some((j2) => j2.id === j.id),
          )
          .some((j) => j.id === s.jobTypeId),
      )
      .map((s) => {
        return { value: s.id, label: s.name };
      }),
    { value: -1, label: "Complete" },
  ];
  const filterOperators = getGridSingleSelectOperators()
    .filter(
      (operator) => operator.value === "isAnyOf" || operator.value === "not",
    )
    .reverse();
  const dateOperators = getGridDateOperators().filter(
    (o) => o.value !== "isEmpty" && o.value !== "isNotEmpty",
  );

  const updateSearch = debounce((query: string) => {
    dispatch(
      searchJob({
        search: { ...reportFilter, query },
        start: reportStart,
        end: reportEnd,
      }),
    ).then((result: any) => {
      dispatch(
        setReportFilter({ ...reportFilter, pageNumber: phone ? 2 : 1, query }),
      );
      setMobileRows(result.payload.results);
    });
  }, 500);

  useEffect(() => {
    if (!phone) {
      dispatch(
        searchJob({ search: reportFilter, start: reportStart, end: reportEnd }),
      );
    } else {
      dispatch(
        searchJob({
          search: { ...reportFilter, pageNumber: 1, pageSize: 10 },
          start: reportStart,
          end: reportEnd,
        }),
      ).then((result: any) => {
        setMobileRows(result.payload.results);
        dispatch(
          setReportFilter({ ...reportFilter, pageNumber: 2, pageSize: 10 }),
        );
      });
    }
    return () => {
      dispatch(setReportFilter(defaultParams));
    };
  }, [dispatch, reportStart, reportEnd, phone]);

  const columns: ColumnDef[] = [
    {
      field: "title",
      headerName: "Title",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      filterable: false,
      mobileSize: 12,
      renderCell: ({ value }) => <>{value.label}</>,
    },
    // {
    //   field: "created",
    //   headerName: "Created",
    //   headerAlign: "center",
    //   align: "center",
    //   flex: 0.5,
    //   headerClassName: "header",
    //   filterable: false,
    //   mobileSize: 12,
    //   renderCell: ({ value }) => <>{value.label}</>,
    // },
    {
      field: "status",
      headerName: "Status",
      headerAlign: "center",
      align: "center",
      sortable: false,
      flex: 1,
      headerClassName: "header",
      renderCell: ({ value }) => <>{value.label}</>,
      type: "singleSelect",
      valueOptions: statusOptions,
      filterOperators: filterOperators,
      mobileSize: 12,
    },
    {
      field: "completedDate",
      headerName: "Completed Date",
      headerAlign: "center",
      align: "center",
      flex: 0.5,
      headerClassName: "header",
      type: "date",
      valueGetter: (value: any) => value?.value ?? null,
      renderCell: ({ row }) => <>{row.completedDate?.label ?? "N/A"}</>,
      filterOperators: dateOperators,
      mobileSize: 12,
    },
    {
      field: "type",
      headerName: "Type",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
      renderCell: ({ value }) => <>{value.label}</>,
      type: "singleSelect",
      valueOptions: jobTypes
        .filter(
          (j) =>
            j.logHours &&
            loggedInUser?.assignedJobTypes?.some((j2) => j2.id === j.id),
        )
        .map((j) => {
          return { label: j.name, value: j.id };
        }),
      filterOperators: filterOperators,
    },
    {
      field: "hours",
      headerName: "Total Hours",
      headerAlign: "center",
      align: "center",
      flex: 0.35,
      headerClassName: "header",
      renderCell: ({ value }) => <>{value.label}</>,
      type: "number",
      filterOperators: getGridNumericOperators().filter(
        (operator) =>
          operator.value === "=" ||
          operator.value === ">" ||
          operator.value === ">=" ||
          operator.value === "<" ||
          operator.value === "<=",
      ),
      mobileSize: 12,
    },
    {
      field: "workedBy",
      headerName: "Worked By",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      renderCell: ({ value }) => <>{value.label}</>,
      type: "singleSelect",
      valueOptions: simpleUserList.map((u) => ({
        label: u.value,
        value: u.id,
      })),
      filterOperators: filterOperators,
      mobileSize: 12,
    },
    {
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      align: "center",
      flex: 0.35,
      headerClassName: "header",
      sortable: false,
      disableColumnMenu: true,
      disableExport: true,
      filterable: false,
      renderCell: (params) => <>{params.value}</>,
      mobileSize: 12,
    },
  ];

  const userNameMap = new Map(simpleUserList.map((u) => [u.id, u.value ?? ""]));

  const displayRows = (array: any[]) => {
    let rowDisplay: {
      id: number;
      title: string | { label: string };
      mobileTitle: string;
      type: string | { label: string | undefined; value: number | undefined };
      // created: string | { label: string };
      status: string | { label: string | undefined; value: number | undefined };
      hours: number | { label: number };
      workedBy: string | { label: string };
      actions: JSX.Element;
    }[] = [];
    array.forEach((job: JobReport) => {
      const workedByLabel = (job.workedByIds ?? [])
        .map((id: number) => userNameMap.get(id))
        .filter(Boolean)
        .join(", ");
      let rowObject = {
        id: job.id,
        title: phone ? job.title : { label: job.title },
        mobileTitle: `${job.title}: ${job.hours} hrs`,
        type: phone
          ? job.jobTypeName
          : { label: job.jobTypeName, value: job.jobTypeId },
        categoryName: job.jobCategory || "",
        // created: phone
        //   ? `${moment(job.createdOn).format("MM/DD/YY")}`
        //   : { label: `${moment(job.createdOn).format("MM/DD/YY")}` },
        status: phone
          ? job.completedOn
            ? `Complete`
            : job.jobStatusName
          : {
              label: job.completedOn ? `Complete` : job.jobStatusName,
              value: job.jobStatusId,
            },
        completedDate: {
          label: job.completedOn
            ? moment(job.completedOn).format("MM/DD/YYYY")
            : "N/A",
          value: job.completedOn ? new Date(job.completedOn) : null,
        },
        hours: phone ? job.hours : { label: job.hours },
        workedBy: phone ? workedByLabel : { label: workedByLabel },
        actions: (
          <Base.TableActions
            id={job.id!}
            handleView={() =>
              window.open(
                `/job/view/${job.id}`,
                "_blank",
                "noopener,noreferrer",
              )
            }
          />
        ),
      };
      rowDisplay.push(rowObject);
    });
    return rowDisplay;
  };

  const rows = displayRows(jobSearchResults?.results ?? []);

  const changePage = (pageNumber?: number, pageSize?: number) => {
    dispatch(
      searchJob({
        search: { ...reportFilter, pageNumber, pageSize },
        start: reportStart,
        end: reportEnd,
      }),
    ).then(() => {
      dispatch(
        setReportFilter({
          ...reportFilter,
          pageNumber,
          pageSize,
        }),
      );
    });
  };

  const getOrderBy = (field: string) => {
    const fieldList = [
      "title",
      "received",
      "type",
      "siteName",
      "hours",
      "completedDate",
    ];
    return fieldList.find((value) => value === field) ?? undefined;
  };

  const handleFilterModelChange = debounce((model: GridFilterModel) => {
    setFilterModel(model);
    const filterList = model.items;
    let filters: string[] = [];

    if (filterList) {
      filterList.forEach((filter) => {
        if (!!filter.value)
          if (Array.isArray(filter.value)) {
            filter.value.forEach((id) => {
              let newFilter = `${filter.field}:${id}`;
              filters.push(newFilter);
            });
          } else {
            let parsedFilter: string = "";
            if (!isNaN(new Date(filter.value).valueOf())) {
              const year = new Date(filter.value).getFullYear();
              // if user is typing the date in, run the search if it's a valid date
              if (year.toString().length === 4) {
                parsedFilter = `${new Date(filter.value).getTime()}`;
              } else return;
            } else {
              parsedFilter = filter.value;
            }
            let newFilter = `${
              filter.operator === "not"
                ? "!"
                : filter.operator === "="
                ? "="
                : filter.operator === ">" || filter.operator === "after"
                ? ">"
                : filter.operator === ">=" || filter.operator === "onOrAfter"
                ? ">="
                : filter.operator === "<" || filter.operator === "before"
                ? "<"
                : filter.operator === "onOrBefore"
                ? "<="
                : ""
            }${filter.field}:${parsedFilter}`;
            filters.push(newFilter);
          }
      });
    }
    dispatch(
      searchJob({
        search: { ...reportFilter, pageNumber: 1, filters },
        start: reportStart,
        end: reportEnd,
      }),
    ).then(() => {
      dispatch(setReportFilter({ ...reportFilter, pageNumber: 1, filters }));
    });
  }, 1000);

  const next = () => {
    dispatch(
      searchJob({
        search: reportFilter,
        start: reportStart!,
        end: reportEnd!,
      }),
    ).then((result: any) => {
      dispatch(
        setReportFilter({
          ...reportFilter,
          pageNumber: reportFilter.pageNumber! + 1,
        }),
      );
      setMobileRows([...mobileRows, ...result.payload.results]);
    });
  };

  const preExport = async () => {
    const response = await dispatch(
      searchJob({
        search: { ...reportFilter, pageSize: jobSearchResults?.totalResults },
        start: reportStart,
        end: reportEnd,
      }),
    );
    return response.payload;
  };

  const filterDefault: {
    filter?: KeyValue;
    name?: string;
    globalShare: boolean;
  } = {
    filter: undefined,
    name: "",
    globalShare: false,
  };

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }} pb={{ sm: 0, xs: 2 }}>
        <Formik
          initialValues={{
            range: undefined,
          }}
          onSubmit={() => {}}
        >
          {({ values, resetForm, setFieldValue }) => (
            <Form noValidate>
              <Base.BorderBox>
                <Grid size={12} px={1}>
                  <Field
                    name="range"
                    label="Date Range (Worked On)"
                    component={Inputs.DateRange}
                    handleChange={(value: any) => {
                      if (
                        value[0] &&
                        date instanceof Date &&
                        !isNaN(value[0]?.valueOf()!)
                      ) {
                        setReportStart(value[0]);
                      }
                      if (
                        value[1] &&
                        date instanceof Date &&
                        !isNaN(value[1]?.valueOf()!)
                      ) {
                        setReportEnd(value[1]);
                      }
                    }}
                  />
                </Grid>
                <Grid size={{ md: 10, xs: 12 }} pb={2} px={1}>
                  <Base.Search
                    label="Search"
                    handleChange={updateSearch}
                    value={reportFilter.query}
                  />
                </Grid>
                <Grid
                  size={{ md: 2, xs: 12 }}
                  pb={2}
                  px={{ md: "15px", xs: 0 }}
                >
                  <Base.Button
                    text="Reset"
                    onClick={() => {
                      resetForm();
                      setReportStart(undefined);
                      setReportEnd(undefined);
                      dispatch(searchJob({ search: defaultParams })).then(
                        () => {
                          dispatch(
                            setReportFilter({
                              ...defaultParams,
                              pageNumber: phone ? 2 : 1,
                            }),
                          );
                        },
                      );
                      setFilterModel({ ...filterModel, items: [] });
                    }}
                    fullWidth
                  />
                </Grid>
              </Base.BorderBox>
            </Form>
          )}
        </Formik>
      </Grid>
      <Grid size={{ xs: 12 }} pb={{ xs: 15, md: 5 }}>
        <Box
          sx={{
            "& .link": {
              cursor: "pointer",
            },
            "& .header": {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            },
          }}
        >
          <Base.Text text="Time Report" variant="h3" />
          <Base.TableContainer
            columns={columns}
            rows={rows}
            mobileRows={displayRows(mobileRows)}
            page={reportFilter.pageNumber}
            pageSize={reportFilter.pageSize}
            totalRows={jobSearchResults?.totalResults}
            changePage={changePage}
            next={next}
            loading={searchStatus === "loading"}
            allowExport
            handleSort={(orderByField, orderDirection) => {
              const orderBy = orderByField
                ? getOrderBy(orderByField)
                : undefined;
              dispatch(
                searchJob({
                  search: { ...reportFilter, orderBy, orderDirection },
                  start: reportStart,
                  end: reportEnd,
                }),
              ).then(() => {
                dispatch(
                  setReportFilter({
                    ...reportFilter,
                    orderBy,
                    orderDirection,
                  }),
                );
              });
            }}
            filterModel={filterModel}
            handleFilterModelChange={handleFilterModelChange}
            columnVisibility={visibility}
            handleColumnVisibilityChange={(model) => setVisibility(model)}
            handleFilterManage={() => {
              setFilterSetModal({ mode: "load" });
              dispatch(getFilterSets());
            }}
            handleSaveFilterSet={() => {
              setFilterSetModal({ mode: "save" });
            }}
            filters={reportFilter}
            reportStart={reportStart}
            reportEnd={reportEnd}
          />
        </Box>
      </Grid>
      <Base.Modal
        open={!!filterSetModal?.mode}
        onClose={() => setFilterSetModal({ mode: undefined })}
      >
        <Paper sx={{ padding: 2, width: { md: 600, xs: "100%" } }}>
          <Formik initialValues={filterDefault} onSubmit={() => {}}>
            {({ values }) => (
              <Form noValidate>
                <Base.Text
                  text={
                    filterSetModal?.mode === "delete"
                      ? `Are you sure you want to delete this filter set?`
                      : `Load Filter Sets`
                  }
                  center
                />
                {filterSetModal?.mode === "load" ? (
                  <Grid container>
                    <Grid size={12}>
                      <Field
                        component={Inputs.Dropdown}
                        name="filter"
                        label="Filter Set"
                        options={filterSets}
                        staticFilter
                      />
                    </Grid>
                    <Grid size={12}>
                      <Grid container justifyContent={"space-between"}>
                        <Grid>
                          <Base.Button
                            text="Cancel"
                            type="secondary"
                            onClick={() =>
                              setFilterSetModal({ mode: undefined })
                            }
                          />
                        </Grid>
                        {values?.filter?.id &&
                          values?.filter?.id > 0 &&
                          values.filter.createdUserId ===
                            loggedInUser?.gmdId && (
                            <Grid>
                              <Base.Button
                                text="Delete"
                                onClick={() =>
                                  setFilterSetModal({ mode: "delete" })
                                }
                              />
                            </Grid>
                          )}
                        <Grid>
                          <Base.Button
                            text="Load"
                            onClick={() => {
                              if (values.filter?.id) {
                                dispatch(getFilterSet(values.filter?.id!)).then(
                                  ({ meta, payload }) => {
                                    if (meta.requestStatus === "fulfilled") {
                                      setFilterSetModal({ mode: undefined });
                                      if (!!payload) {
                                        const filterSet = payload as FilterSet;
                                        const vis = JSON.parse(
                                          filterSet.visibleColumns,
                                        ) as GridColumnVisibilityModel;
                                        const filter = JSON.parse(
                                          filterSet.filters,
                                        ) as GridFilterItem[];
                                        setVisibility(vis);
                                        setFilterModel({
                                          items: filter,
                                          logicOperator: GridLogicOperator.And,
                                        });
                                        handleFilterModelChange({
                                          items: filter,
                                          logicOperator: GridLogicOperator.And,
                                        });
                                      }
                                    }
                                  },
                                );
                              }
                            }}
                            disabled={filterSetStatus === "loading"}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                ) : filterSetModal?.mode === "delete" ? (
                  <Grid container>
                    <Grid size={12}>
                      <Grid container justifyContent={"space-between"}>
                        <Grid>
                          <Base.Button
                            text="Cancel"
                            type="secondary"
                            onClick={() =>
                              setFilterSetModal({ mode: undefined })
                            }
                          />
                        </Grid>
                        <Grid>
                          <Base.Button
                            text="Delete"
                            onClick={() => {
                              dispatch(
                                deleteFilterSet(values.filter?.id!),
                              ).then(({ meta }) => {
                                if (meta.requestStatus === "fulfilled") {
                                  setFilterSetModal({ mode: undefined });
                                }
                              });
                            }}
                            disabled={filterSetStatus === "loading"}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                ) : filterSetModal?.mode === "save" ? (
                  <Grid container spacing={2}>
                    <Grid size={12}>
                      <Field
                        component={Inputs.Text}
                        name="name"
                        required
                        label="Filter Set Name"
                      />
                    </Grid>
                    <Grid size={12}>
                      <Field
                        component={Inputs.Checkbox}
                        name="globalShare"
                        required
                        label="Share With All Users?"
                      />
                    </Grid>
                    <Grid size={12}>
                      <Grid container justifyContent={"space-between"}>
                        <Grid>
                          <Base.Button
                            text="Cancel"
                            type="secondary"
                            onClick={() =>
                              setFilterSetModal({ mode: undefined })
                            }
                          />
                        </Grid>
                        <Grid>
                          <Base.Button
                            text="Save"
                            onClick={() => {
                              dispatch(
                                postFilterSet({
                                  id: 0,
                                  name: `${values.name}`,
                                  filters: JSON.stringify(filterModel.items),
                                  visibleColumns: JSON.stringify(visibility),
                                  globalShare: values.globalShare,
                                }),
                              ).then(({ meta }) => {
                                if (meta.requestStatus === "fulfilled") {
                                  setFilterSetModal({ mode: undefined });
                                }
                              });
                            }}
                            disabled={
                              filterSetStatus === "loading" || !values.name
                            }
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                ) : (
                  <></>
                )}
              </Form>
            )}
          </Formik>
        </Paper>
      </Base.Modal>
    </Grid>
  );
};

export default ReportJobTable;
